import { useState } from 'react';
import { Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import {
  calculateManualItemTotal,
  MANUAL_ITEM_TYPES,
  MANUAL_ITEM_UNITS,
  validateManualItemInput,
  type ManualEstimateItem,
  type ManualItemInput,
  type ManualItemType,
  type ManualItemUnit,
} from '@/domain/manualEstimateItems';
import { safeFormatPLN } from '@/infra/utils';

const EMPTY_FORM: ManualItemForm = {
  name: '',
  quantity: '1',
  unit: 'szt.',
  unitPrice: '',
  itemType: 'labor',
};

const TYPE_LABELS: Record<ManualItemType, string> = {
  labor: 'Robocizna',
  material: 'Materiał',
  auxiliary: 'Pomocnicze',
};

interface ManualItemForm {
  name: string;
  quantity: string;
  unit: ManualItemUnit;
  unitPrice: string;
  itemType: ManualItemType;
}

interface ManualItemsEditorProps {
  items: ManualEstimateItem[];
  onAdd: (input: ManualItemInput) => void;
  onUpdate: (id: string, input: ManualItemInput) => void;
  onRemove: (id: string) => void;
}

function toNumber(value: string): number {
  return Number(value.replace(',', '.'));
}

function toForm(item: ManualEstimateItem): ManualItemForm {
  return {
    name: item.label,
    quantity: String(item.quantity),
    unit: item.unit as ManualItemUnit,
    unitPrice: String(item.unitRateNetto),
    itemType: item.itemType,
  };
}

export function ManualItemsEditor({ items, onAdd, onUpdate, onRemove }: ManualItemsEditorProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const quantity = toNumber(form.quantity);
  const unitPrice = toNumber(form.unitPrice);
  const previewTotal = calculateManualItemTotal(quantity, unitPrice);

  function resetForm(): void {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setErrorMessage(null);
  }

  function submit(): void {
    const input: ManualItemInput = {
      name: form.name,
      quantity,
      unit: form.unit,
      unitPrice,
      itemType: form.itemType,
    };
    const validationMessage = validateManualItemInput(input);
    if (validationMessage) {
      setErrorMessage(validationMessage);
      return;
    }
    if (editingId) onUpdate(editingId, input);
    else onAdd(input);
    resetForm();
  }

  return (
    <section className="mt-3 rounded-2xl border border-[#2a2f38] bg-[#0f1217] p-4">
      <div>
        <p className="text-sm font-semibold text-white">Własne pozycje oferty</p>
        <p className="mt-1 text-xs text-slate-400">Pozycja trafi do ceny i PDF dopiero po kliknięciu Dodaj pozycję.</p>
      </div>

      {items.length > 0 && (
        <div className="mt-3 grid gap-2">
          {items.map((item) => (
            <div key={item.id} className="rounded-xl border border-[#2a2f38] bg-[#15191f] p-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-semibold text-white">{item.label}</p>
                  <p className="mt-1 text-sm text-slate-300">
                    {item.quantity} {item.unit} × {safeFormatPLN(item.unitRateNetto)} · {TYPE_LABELS[item.itemType]}
                  </p>
                  <p className="mt-1 font-black text-[#00e676]">{safeFormatPLN(item.totalNetto)}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setForm(toForm(item));
                      setEditingId(item.id);
                      setErrorMessage(null);
                    }}
                    className="flex min-h-14 items-center justify-center gap-2 rounded-xl border border-cyan-400/30 px-4 font-bold text-cyan-100"
                  >
                    <Pencil className="h-4 w-4" /> Edytuj
                  </button>
                  <button
                    type="button"
                    onClick={() => { onRemove(item.id); }}
                    className="flex min-h-14 items-center justify-center gap-2 rounded-xl border border-red-400/30 px-4 font-bold text-red-200"
                  >
                    <Trash2 className="h-4 w-4" /> Usuń
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="sm:col-span-2">
          <span className="mb-1 block text-xs font-bold text-slate-300">Nazwa</span>
          <input value={form.name} onChange={(event) => { setForm((current) => ({ ...current, name: event.target.value })); }} className="min-h-12 w-full rounded-lg border border-[#2a2f38] bg-[#15191f] px-3 text-white" placeholder="np. Spocznik kamienny" />
        </label>
        <label>
          <span className="mb-1 block text-xs font-bold text-slate-300">Ilość</span>
          <input type="number" min="0" step="0.01" inputMode="decimal" value={form.quantity} onChange={(event) => { setForm((current) => ({ ...current, quantity: event.target.value })); }} className="min-h-12 w-full rounded-lg border border-[#2a2f38] bg-[#15191f] px-3 text-white" />
        </label>
        <label>
          <span className="mb-1 block text-xs font-bold text-slate-300">Jednostka</span>
          <select value={form.unit} onChange={(event) => { setForm((current) => ({ ...current, unit: event.target.value as ManualItemUnit })); }} className="min-h-12 w-full rounded-lg border border-[#2a2f38] bg-[#15191f] px-3 text-white">
            {MANUAL_ITEM_UNITS.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
          </select>
        </label>
        <label>
          <span className="mb-1 block text-xs font-bold text-slate-300">Cena jednostkowa netto</span>
          <input type="number" min="0" step="0.01" inputMode="decimal" value={form.unitPrice} onChange={(event) => { setForm((current) => ({ ...current, unitPrice: event.target.value })); }} className="min-h-12 w-full rounded-lg border border-[#2a2f38] bg-[#15191f] px-3 text-white" placeholder="0,00" />
        </label>
        <label>
          <span className="mb-1 block text-xs font-bold text-slate-300">Typ</span>
          <select value={form.itemType} onChange={(event) => { setForm((current) => ({ ...current, itemType: event.target.value as ManualItemType })); }} className="min-h-12 w-full rounded-lg border border-[#2a2f38] bg-[#15191f] px-3 text-white">
            {MANUAL_ITEM_TYPES.map((type) => <option key={type} value={type}>{TYPE_LABELS[type]}</option>)}
          </select>
        </label>
      </div>

      <p className="mt-3 text-sm font-bold text-slate-200">Wartość pozycji: {safeFormatPLN(previewTotal)}</p>
      {errorMessage && <p className="mt-2 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm font-semibold text-red-200">{errorMessage}</p>}
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <button type="button" onClick={submit} className="flex min-h-14 items-center justify-center gap-2 rounded-2xl bg-[#00e676] px-4 font-black text-black">
          {editingId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {editingId ? 'Zapisz zmiany' : 'Dodaj pozycję'}
        </button>
        {editingId && (
          <button type="button" onClick={resetForm} className="flex min-h-14 items-center justify-center gap-2 rounded-2xl border border-[#2a2f38] px-4 font-bold text-slate-200">
            <X className="h-4 w-4" /> Anuluj edycję
          </button>
        )}
      </div>
    </section>
  );
}
